import { RoomAcl } from '@era/locations-sdk';
import { Group, User } from '@era/security-sdk';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import type {
  NamedFloorAclGroups,
  NamedFloorAclMembers,
  NamedLocationAclGroups,
  NamedLocationAclMembers,
  NamedRoomAcl,
  NamedRoomAclGroups,
  NamedRoomAclMembers,
} from '@rocksoft/shared';
import * as groupSelector from '../group/group.selector';
import * as aclRouterSelector from '../router/acl-router.selector';
import * as locationRouterSelector from '../router/locations-router.selector';
import * as userSelector from '../user/user.selector';
import * as fromRoomAcl from './room-acl.reducer';
import { RoomAclState } from './room-acl.state';

export const selectRoomAclState = createFeatureSelector<RoomAclState>('roomAcls');

export const selectRoomAclIds = createSelector(selectRoomAclState, fromRoomAcl.selectRoomAclIds);

export const selectAllRoomAcls = createSelector(selectRoomAclState, fromRoomAcl.selectAllRoomAcls);

export const selectRoomAcls = createSelector(selectRoomAclState, fromRoomAcl.selectRoomAcls);

export const selectRoomAclLoaded = createSelector(
  selectRoomAcls,
  (roomAcls: Dictionary<RoomAcl>) => roomAcls && Object.keys(roomAcls).length > 0
);

export const selectRoomAcl = (resourceId: string) =>
  createSelector(selectRoomAcls, (roomAcls: Dictionary<RoomAcl>) => (roomAcls ? roomAcls[resourceId] : undefined));

export const selectCurrentRoomAcl = createSelector(
  selectRoomAcls,
  locationRouterSelector.selectRoomId,
  (roomAcls: Dictionary<RoomAcl>, roomId: string) => (roomAcls ? roomAcls[roomId] : undefined)
);

export const selectCurrentRoomAclGroup = createSelector(
  selectCurrentRoomAcl,
  aclRouterSelector.selectCurrentAclId,
  (roomAcl, aclGroupId) =>
    roomAcl && aclGroupId ? (roomAcl.groups ?? []).find((x) => x.groupId === aclGroupId) : undefined
);

export const selectCurrentRoomAclMember = createSelector(
  selectCurrentRoomAcl,
  aclRouterSelector.selectCurrentAclId,
  (roomAcl, aclUserId) => (roomAcl && aclUserId ? (roomAcl.users ?? []).find((x) => x.member === aclUserId) : undefined)
);

export const selectCurrentRoomAclWithNames = createSelector(
  selectCurrentRoomAcl,
  userSelector.selectAllUsers,
  groupSelector.selectAllGroups,
  (roomAcl: RoomAcl, users: User[], groups: Group[]) => {
    const findUserName = (id: string) => {
      const user = users.find((x) => x.id === id);
      return user ? user.displayName : undefined;
    };

    const findGroupName = (id: string) => {
      const group = groups.find((x) => x.id === id);
      return group ? group.displayName : undefined;
    };

    if (roomAcl && users && groups) {
      if (roomAcl) {
        const roomAclGroups: NamedRoomAclGroups[] = (roomAcl.groups ?? []).map((x) => ({
          ...x,
          name: findGroupName(x.groupId) ?? '',
        }));
        const roomAclMembers: NamedRoomAclMembers[] = (roomAcl.users ?? []).map((x) => ({
          ...x,
          name: findUserName(x.member) ?? '',
        }));
        const floorAclGroups: NamedFloorAclGroups[] = (roomAcl.floorAcl?.groups ?? []).map((x) => ({
          ...x,
          name: findGroupName(x.groupId) ?? '',
        }));
        const floorAclMembers: NamedFloorAclMembers[] = (roomAcl.floorAcl?.users ?? []).map((x) => ({
          ...x,
          name: findUserName(x.member) ?? '',
        }));
        const locationAclGroups: NamedLocationAclGroups[] = (roomAcl.floorAcl?.locationAcl?.groups ?? []).map((x) => ({
          ...x,
          name: findGroupName(x.groupId) ?? '',
        }));
        const locationAclMembers: NamedLocationAclMembers[] = (roomAcl.floorAcl?.locationAcl?.users ?? []).map((x) => ({
          ...x,
          name: findUserName(x.member) ?? '',
        }));

        const result: NamedRoomAcl = {
          ...roomAcl,
          resourceId: roomAcl.resourceId ?? '',
          groups: roomAclGroups,
          users: roomAclMembers,
          floorAcl: {
            ...roomAcl.floorAcl,
            resourceId: roomAcl.floorAcl?.resourceId ?? '',
            groups: floorAclGroups,
            users: floorAclMembers,
            locationAcl: {
              ...roomAcl.floorAcl?.locationAcl,
              resourceId: roomAcl.floorAcl?.locationAcl?.resourceId ?? '',
              groups: locationAclGroups,
              users: locationAclMembers,
            },
          },
        };

        return result;
      }
    }
    return undefined;
  }
);

export const selectCurrentRoomAclGroups = (groupIds: string[]) =>
  createSelector(selectCurrentRoomAclWithNames, (roomAcls) => {
    if (roomAcls && groupIds) {
      const roomAclMembers = (roomAcls.groups ?? []).filter((x) => groupIds.includes(x.groupId));
      return roomAclMembers ?? undefined;
    }
    return undefined;
  });
